import { useState, useCallback, useMemo } from 'react';
import {
  QUESTION_TYPES,
  VALIDATION_TYPES,
  useSelectedItem,
  getFormBuilderActions,
} from '@/features/quote-and-apply-builder';

interface UseQuestionFieldParams {
  section: PolicyFormSection;
  subsection: PolicyFormSubSection;
  group: PolicyFormQuestionGroup;
  question: PolicyFormQuestion;
}

export function useQuestionField({ section, subsection, group, question }: UseQuestionFieldParams) {
  const [showSettings, setShowSettings] = useState(false);
  const selectedItem = useSelectedItem();
  const { setSelectedItem, updateQuestion } = getFormBuilderActions();

  const isSelected = selectedItem?.type === 'question' && selectedItem.id === question.questionRef;

  const handleSelect = useCallback(() => {
    setSelectedItem({
      type: 'question',
      id: question.questionRef,
      sectionId: section.id,
      subsectionId: subsection.id,
      groupId: group.id,
    });
  }, [setSelectedItem, question.questionRef, section.id, subsection.id, group.id]);

  const handleSaveSettings = useCallback(
    (updates: Partial<PolicyFormQuestion>) => {
      updateQuestion(question.questionRef, updates);
    },
    [updateQuestion, question.questionRef],
  );

  const handleOpenSettings = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setShowSettings(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  const typeLabel = useMemo(() => {
    return (
      QUESTION_TYPES.find(questionType => questionType.value === question.type)?.label ||
      question.type
    );
  }, [question.type]);

  const validationLabel = useMemo(() => {
    if (!question.validationType) return null;
    return (
      VALIDATION_TYPES.find(validationType => validationType.value === question.validationType)
        ?.label || question.validationType
    );
  }, [question.validationType]);

  const hasConditions = useMemo(() => {
    return !!(
      question.conditional?.visible ||
      question.conditional?.required ||
      question.conditional?.showConditionalText
    );
  }, [question.conditional]);

  const isGridFormat = group.format === 'grid';

  return {
    showSettings,
    isSelected,
    typeLabel,
    validationLabel,
    hasConditions,
    isGridFormat,
    handleSelect,
    handleSaveSettings,
    handleOpenSettings,
    handleCloseSettings,
  };
}
